import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureInteger } from "../../common/utils/generalUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { createMapSelector, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { dateRangeOverlap, maxDate, minDate, nowISOString, parseDate } from "../../common/utils/dateUtils";
import { deepEquals } from "../../common/utils/objectUtilities";
import { logout } from "../../common/reducers/initContext";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import moment from "moment";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { getDisplayActualTimes, prepareRosterArray } from "./rosterSliceUtils";


const key = roster => ensureInteger(roster.rosterid);

const updateState = (state, roster) => {
    state.data[key(roster)] = roster;
}
const removeState = (state, rosterid) => {
    delete state.data[rosterid];
}

export const rosterClientSlice = createSlice({
    name: 'clientRosters',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toProcess.delete(key(roster));
            })
            toProcess.forEach(id => delete state.data[id]);
        },
        setForGroups: (state, action) => {
            const groupids = new Set(action.payload.groupids);
            const toRemove = new Set(Object.values(state.data).filter(r => groupids.has(r.groupid)).map(r => key(r)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        setForShifts: (state, action) => {
            const shiftids = new Set(action.payload.shiftids);
            const toRemove = new Set(Object.values(state.data).filter(r => shiftids.has(r.shiftid)).map(r => key(r)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(r => {
                prepareRosterArray(action.payload).forEach(r => state.data[key(r)] = r);
            });
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(shift => {
                for (const roster of Object.values(state.data)) {
                    if (roster.shiftid === shift.id) {
                        const { display_actual_start, display_actual_end } = getDisplayActualTimes(roster);
                        
                        roster.shift_start = shift.start;
                        roster.shift_end = shift.end;
                        roster.shift_travel = shift.travel;
                        roster.display_actual_start = display_actual_start;
                        roster.display_actual_end = display_actual_end;
                    }
                }
            });
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(roster => delete state.data[key(roster)])
        },
        extraReducers: builder => {
            builder.addCase(logout, () => rosterClientSlice.getInitialState());
        }
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'roster_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'roster_one');

const fetchRoster = aggregatingFetch(
    id => network => network.get('/api/v2/clientRoster/' + encodeURIComponent(id), data => rosterClientSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/clientRosters', ids, data => rosterClientSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchGroupRoster = aggregatingFetch(
    groupid => network => network.get('/api/v2/clientRostersForGroup/' + encodeURIComponent(groupid), data => rosterClientSlice.actions.setForGroups({ groupids: [groupid], data }), visualizeFailure('get')),
    groupids => network => network.post('/api/v2/clientRostersForGroups', groupids, data => rosterClientSlice.actions.setForGroups({ groupids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchShiftRosters = aggregatingFetch(
    shiftid => network => network.get('/api/v2/clientRostersForShift/' + encodeURIComponent(shiftid), data => rosterClientSlice.actions.setForShifts({ shiftids: [shiftid], data }), visualizeFailure('get')),
    shiftids => network => network.post('/api/v2/clientRostersForShifts', shiftids, data => rosterClientSlice.actions.setForShifts({ shiftids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchMemberRostersInRange = (memberid, start, end) => network => network.get(`/api/v2/clientRosters/member/${encodeURIComponent(memberid)}/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, rosterClientSlice.actions.addOrUpdate, visualizeFailure('get'));

const fetchRostersInRange = aggregatingFetch(
    (start, end) => network => {
        return network.get(`/api/v2/clientRosters/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, rosterClientSlice.actions.addOrUpdate, visualizeFailure('get'))
    },
    keys => network => {
        return network.get(`/api/v2/clientRosters/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, rosterClientSlice.actions.addOrUpdate, visualizeFailure('get'))
    }
)

export const fetchUnconfirmedRostersInRange = (start, end, config) => network => network.post('/api/v2/clientRosterUnconfirmed', {start, end}, rosterClientSlice.actions.addOrUpdate, visualizeFailure('get'))

// #endregion
const selectRawRosters = state => state.clientRosters.data;
const selectDataByShift = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.shiftid]) {
                res[r.shiftid] = [];
            }
            res[r.shiftid].push(r);
            return res;
        }, {});
    }
);
const selectDataByGroup = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.groupid]) {
                res[r.groupid] = [];
            }
            res[r.groupid].push(r);
            return res;
        }, {});
    }
);

const selectShiftRosters = shiftidsInput => createSelector([selectDataByShift], shiftRosters => {
    const shiftids = ensureArray(shiftidsInput);
    return shiftids.flatMap(shiftid => shiftRosters[shiftid] ?? []);
 });

const selectGroupRosters = groupidsInput => createSelector([selectDataByGroup], groupRosters => {
    const groupids = ensureArray(groupidsInput);
    return groupids.flatMap(groupid => groupRosters[groupid] ?? []);
});

const selectMemberRostersInRange = (memberid, start, end) => createSelector([selectRawRosters], rosters => Object.values(rosters)?.filter(r => r.memberid === memberid && dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? [])
const selectRostersInRange = (start, end) => createSelector([selectRawRosters], rosters => Object.values(rosters)?.filter(r => dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? []);
// #region selectors

// #endregion

const listen = {
    rosterClientCreated: (roster, { dispatch, websocket }) => {
        markFetched(websocket, 'roster', roster);
        dispatch(rosterClientSlice.actions.addOrUpdate(roster))
    },
    rosterClientUpdated: (roster, { dispatch }) => dispatch(rosterClientSlice.actions.addOrUpdate(roster)),
    rosterClientRemoved: (roster, { dispatch }) => dispatch(rosterClientSlice.actions.remove(roster)),
    shiftClientUpdated: (shift, { dispatch }) => dispatch(rosterClientSlice.actions.updateShift(shift))
}

export const memberRequiresRosterConfirmation = (member, shift, roster, memberRequiresRosterConfirmation) => memberRequiresRosterConfirmation(moment, { ...(JSON.parse(JSON.stringify(member))), shift_customfields: shift?.customfields ?? {}, ...roster })

const options = {
    cleanup: { root: rosterClientSlice.name, all: state => Object.values(selectRawRosters(state)), removeAll: rosterClientSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('client roster viewer')
}
const rosterInitByID = initMapFactory(fetchRoster, listen, { name: 'clientRosterByID' });
registerEntityBasedFetch('roster', rosterInitByID);

const rosterInitByShift = queryMapFactory(fetchShiftRosters, 'rosterid', rosterInitByID, listen);
registerEntityBasedFetch('shift', rosterInitByShift);

export const [useClientRostersForShift] = dataHookWithCredentialsFactory(selectShiftRosters, rosterInitByShift, options);
export const [useClientRostersForShifts] = dataHookWithCredentialsFactory(selectShiftRosters, initMultiMapFactory(rosterInitByShift, listen), options);

const rosterInitByGroup = queryMapFactory(fetchGroupRoster, 'rosterid', rosterInitByID, listen, { name: 'clientRostersForGroup' });
registerEntityBasedFetch('group', rosterInitByGroup);

export const [useClientRostersForGroup] = dataHookWithCredentialsFactory(selectGroupRosters, rosterInitByGroup, options);
export const [useClientRostersForGroups] = dataHookWithCredentialsFactory(selectGroupRosters, initMultiMapFactory(rosterInitByGroup, listen), options);
export const [useClientMemberRostersInRange] = dataHookWithCredentialsFactory(selectMemberRostersInRange, queryMapFactory(fetchMemberRostersInRange, 'rosterid', rosterInitByID, listen), options);
export const [useClientRostersInRange] = dataHookWithCredentialsFactory(selectRostersInRange, queryMapFactory(fetchRostersInRange, 'rosterid', rosterInitByID, listen), options);

export const canRouteClientRosterView = ({ credentials }) => credentials.hasRole(['client roster viewer']);

const reducer = { name: rosterClientSlice.name, reducer: rosterClientSlice.reducer }
export default reducer;